import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import { brandUrl, webappURL } from '../../util/url';
import Error from '../../component/error/Error';
import Loading from '../../component/loading/Loading';

import { CATEGORY_ALL } from '../discover/discoverConstants';
import NotFound from "../notFound/NotFound";

import BrandOpenGraph from './BrandOpenGraph';
import BrandTwitterCard from './BrandTwitterCard';
import BrandHero from './components/BrandHero';

import PropTypes from 'prop-types';
import { SCHEMA_CONTEXT, brandSchemaOrg } from "../../util/schemaOrg";
import { useBrandWithSimilar } from "./hook/useBrandWithSimilar";
import HorizontalBrandList from "../../components/HorizontalBrandList/HorizontalBrandList";
import { FilterStoreProvider } from '../search/searchFilterPanel/FilterStoreProvider';
import BrandFiltersAndResults from './components/BrandFiltersAndResults.jsx';

const BRAND_CATEGORIES = gql`
    query getCategories($brands: [String!]) {
        getCategories(brands: $brands, excludeEmpties: true, limit: 1000)  {
            name
        }
    }
`;

/**
 * Category brand component.
 * @param hostname
 */
export default function Brand({hostname}) {
    const {
        brandName: partiallyEncodedBrandName,
        categoryName: partiallyEncodedCategoryName = CATEGORY_ALL.name // default parameter value
    } = useParams();

    //get querystring isSearch
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isSearch = queryParams.get('isSearch');

    let brandName;
    try {
        brandName = decodeURIComponent(partiallyEncodedBrandName)
    } catch (error) {
        brandName = partiallyEncodedBrandName;
    }

    let categoryName;
    try {
        categoryName = decodeURIComponent(partiallyEncodedCategoryName)
    } catch (error) {
        categoryName = partiallyEncodedCategoryName;
    }

    // useBrandWithSimilar 훅 사용
    const { 
        brand, 
        similarBrands, 
        loading: brandLoading, 
        error: brandError 
    } = useBrandWithSimilar(brandName);

    // fetch categories
    const { loading: categoryLoading, error: categoryError, data: categoryData } = useQuery(
        BRAND_CATEGORIES,
        {
            variables: {
                brands: [brandName]
            },
            skip: !brandName
        }
    );

    if (brandLoading || categoryLoading) {
        return <Loading />;
    } else if (brandError || categoryError) {
        return <Error error={brandError || categoryError} />;
    } else if (!brand) {
        return <NotFound />;
    } else if (brand.code && brand.code !== 200) {
        return <NotFound />;
    }

    // add client-side pseudo-categories
    const allCategories = categoryData ?
        [CATEGORY_ALL, ...categoryData.getCategories] :
        null;

    // find the selected category matching with the categoryName route parameter
    const selectedCategory = allCategories ?
        allCategories.find(category => categoryName === category.name) :
        null;
    if (allCategories && !selectedCategory) {
        return <NotFound />;
    }


    // meta tag
    const metaTitle = brand.name_kr
        ? `${brand.name_kr}(${brand.name}) | 후루츠패밀리 - 빈티지 세컨핸드 패션 중고거래`
        : `${brand.name} | 후루츠패밀리 - 빈티지 세컨핸드 패션 중고거래`;
    const metaDescription = `구하기 힘든 ${brand.name_kr || brand.name} 아이템, 후루츠패밀리에서 모아보세요. 남성 · 여성 의류, 가방, 주얼리, 신발, 악세서리까지 안전하게 거래하세요.` +
        (brand.description ? ` | ${brand.description}` : '');

    return (
        <section className="Brand">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={webappURL(hostname) + brandUrl(brand.name, selectedCategory)} />
                {!brandLoading && (
                    <script type="application/ld+json">
                        {JSON.stringify(brandSchemaOrg(brand, SCHEMA_CONTEXT, webappURL(hostname)))}
                    </script>
                )}
                <link rel="alternate" type="application/atom+xml" href={`${brandUrl(brand.name, selectedCategory)}/feed.atom`} />
            </Helmet>
            <BrandOpenGraph brand={brand} selectedCategory={selectedCategory} title={metaTitle} description={metaDescription} hostname={hostname}/>
            <BrandTwitterCard brand={brand} selectedCategory={selectedCategory} title={metaTitle} description={metaDescription} />

            <BrandHero brand={brand} />

            <HorizontalBrandList
                brands={similarBrands}
                title="함께 즐겨찾는 브랜드"
                brandRadar={true}
                brandName={brand.name}
            />

            <FilterStoreProvider initialFilters={{ brands: [brand.name] }}>
                <BrandFiltersAndResults
                    brandName={brand.name}
                    productCount={brand.product_count}
                    selectedCategory={selectedCategory}
                    isSearchFlag={isSearch !== null}
                />
            </FilterStoreProvider>
        </section>
    );
}
Brand.propTypes = {
    hostname: PropTypes.string.isRequired,
};
